import { useState, useEffect } from 'react';
import { EnterpriseLayout } from '../components/EnterpriseLayout';
import { Button } from '@zaogig/ui/button';
import { Badge } from '@zaogig/ui/badge';
import { Plus, Edit2, Users } from 'lucide-react';
import { apiClient } from '@zaogig/auth';

export interface JobItem {
  id: string;
  title: string;
  status: 'published' | 'draft' | 'completed' | 'offline';
  appliedCount: number;
  totalNeeded: number;
  salary: string;
  createdAt: string;
}

export default function JobManagement() {
  const [jobs, setJobs] = useState<JobItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadJobs = async () => {
      setLoading(true);
      try {
        const data = await apiClient.get<JobItem[]>('/jobs');
        setJobs(data);
      } catch (error) {
        console.error('Data loading error:', error);
        setJobs(getMockJobs());
      } finally {
        setLoading(false);
      }
    };
    loadJobs();
  }, []);

  const getStatusBadge = (status: JobItem['status']) => {
    const configs = {
      published: { label: '招募中', class: 'bg-green-100 text-green-700 border-green-200' },
      draft: { label: '草稿', class: 'bg-slate-100 text-slate-600 border-slate-200' },
      completed: { label: '已完成', class: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
      offline: { label: '已下架', class: 'bg-red-100 text-red-700 border-red-200' },
    };
    const config = configs[status];
    return <Badge className={`${config.class} border shadow-none font-medium`}>{config.label}</Badge>;
  };

  return (
    <EnterpriseLayout>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">任务管理</h1>
          <p className="text-slate-500 mt-1">发布、编辑及监控您的零工任务执行状态。</p>
        </div>
        <Button className="bg-indigo-600 hover:bg-indigo-700 rounded-xl h-12 px-6 shadow-indigo-200 shadow-lg">
          <Plus className="mr-2 h-5 w-5" /> 发布新任务
        </Button>
      </div>

      <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-slate-500">加载中...</div>
        ) : (
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50/50 border-b border-slate-100">
                <th className="px-6 py-4 text-sm font-semibold text-slate-600">任务名称</th>
                <th className="px-6 py-4 text-sm font-semibold text-slate-600">状态</th>
                <th className="px-6 py-4 text-sm font-semibold text-slate-600">报名进度</th>
                <th className="px-6 py-4 text-sm font-semibold text-slate-600">薪资待遇</th>
                <th className="px-6 py-4 text-sm font-semibold text-slate-600">创建时间</th>
                <th className="px-6 py-4 text-sm font-semibold text-slate-600 text-right">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {jobs.map((job) => (
                <tr key={job.id} className="hover:bg-slate-50/50 transition-colors group">
                  <td className="px-6 py-5">
                    <span className="text-base font-bold text-slate-800">{job.title}</span>
                  </td>
                  <td className="px-6 py-5">{getStatusBadge(job.status)}</td>
                  <td className="px-6 py-5">
                    <div className="flex items-center gap-2">
                      <div className="flex -space-x-2">
                        {[1, 2, 3].map(i => (
                          <div key={i} className="h-7 w-7 rounded-full border-2 border-white bg-slate-200 flex items-center justify-center text-[10px] text-slate-500">U</div>
                        ))}
                      </div>
                      <span className="text-sm font-medium text-slate-600">{job.appliedCount} / {job.totalNeeded}</span>
                    </div>
                  </td>
                  <td className="px-6 py-5 text-sm font-bold text-indigo-600">{job.salary}</td>
                  <td className="px-6 py-5 text-sm text-slate-500">{job.createdAt}</td>
                  <td className="px-6 py-5 text-right">
                    <div className="flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-indigo-600"><Users size={18} /></Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-indigo-600"><Edit2 size={16} /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </EnterpriseLayout>
  );
}

function getMockJobs(): JobItem[] {
  return [
    { id: '1', title: '电商大促-快递分拣员', status: 'published', appliedCount: 15, totalNeeded: 20, salary: '25/小时', createdAt: '2024-05-01' },
    { id: '2', title: '智能车间-装配技工', status: 'published', appliedCount: 8, totalNeeded: 10, salary: '220/天', createdAt: '2024-05-03' },
    { id: '3', title: '展会现场-安保支持', status: 'completed', appliedCount: 5, totalNeeded: 5, salary: '180/天', createdAt: '2024-04-25' },
    { id: '4', title: '周末商场-促销引导', status: 'draft', appliedCount: 0, totalNeeded: 12, salary: '20/小时', createdAt: '2024-05-07' },
  ];
}
